'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Shield, Lock, ArrowRight, User, Loader2, Link2Off, KeyRound, Fingerprint } from 'lucide-react';
import { LiquidAmbientBackground } from '@/components/ui/LiquidGlass';
import TurnstileWidget from '@/components/security/TurnstileWidget';
import SuspensionModal from '@/components/security/SuspensionModal';
import { startAuthentication } from '@simplewebauthn/browser';

export default function MasterLoginPage() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

    // Modal de suspensión si la cuenta está bloqueada por plazo de 7 días
    const [suspensionData, setSuspensionData] = useState<{ isOpen: boolean; operatorId?: string; username?: string }>({
        isOpen: false
    });

    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.classList.remove('dark');
            document.documentElement.classList.add('light');
            try {
                localStorage.setItem('theme', 'light');
            } catch (_) {}
        }
    }, []);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!turnstileToken) {
            toast.error('Por favor complete la verificación de seguridad anti-bot antes de continuar.', { icon: '🛡️' });
            return;
        }

        setIsLoading(true);

        try {
            const res = await fetch('/api/auth/master/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: username.trim(),
                    password,
                    turnstile_token: turnstileToken
                })
            });

            const data = await res.json();

            if (!res.ok) {
                // Handling failures explicitly (Fail-Closed, 3-strikes, etc.)
                if (res.status === 423) {
                    if (data.requires_dual_control) {
                        setSuspensionData({
                            isOpen: true,
                            operatorId: data.operator_id,
                            username: username.trim()
                        });
                        return;
                    }
                    toast.error('ACCESO DENEGADO: Protocolo anti-fuerza bruta activado. Suspensión temporal en curso.', { duration: 6000, icon: '🛑' });
                } else if (res.status === 403 && data.code === 'BOT_DEFENSE_REJECTED') {
                    toast.error('Verificación perimetral rechazada. Por favor reintente el captcha.', { icon: '🛡️' });
                    setTurnstileToken(null);
                } else if (res.status === 500) {
                    toast.error('ERROR CRÍTICO: Fallo en infraestructura o auditoría de red. Transacción abortada.', { icon: '🚨' });
                } else {
                    toast.error(`Error: ${data.error || 'Credenciales inválidas.'}`, { duration: 4000 });
                }
                return;
            }

            // Success Path
            if (data.mfa_required) {
                toast.success('Desafío superado. Requiriendo segundo factor...', { icon: '🔐' });
                window.location.href = '/master/mfa';
            } else {
                toast.success('Acceso concedido al Escudo de Tesorería Root.', { icon: '🏛️' });
                window.dispatchEvent(new Event('auth_changed'));
                window.location.href = '/master';
            }

        } catch (error: any) {
            console.error("Master Login Network Error", error);
            toast.error("Fallo de conexión. El escudo perimetral rechaza la comunicación.", { icon: <Link2Off /> });
        } finally {
            setIsLoading(false);
        }
    };

    // Inicio de sesión con Passkey Biométrica FIDO2
    const handlePasskeyLogin = async () => {
        setIsPasskeyLoading(true);
        const notifyId = toast.loading('Iniciando handshake biométrico FIDO2...');

        try {
            // 1. Obtener opciones del servidor
            const optRes = await fetch('/api/auth/master/passkeys/auth-options', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: username.trim() || undefined,
                    turnstile_token: turnstileToken || undefined
                })
            });

            const optData = await optRes.json();
            if (!optRes.ok || !optData.success) {
                throw new Error(optData.error || 'Error obteniendo desafío biométrico');
            }

            // 2. Invocar WebAuthn en el navegador
            const assertion = await startAuthentication({ optionsJSON: optData.options });

            // 3. Enviar aserción para verificación
            const verifyRes = await fetch('/api/auth/master/passkeys/auth-verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    response: assertion,
                    turnstile_token: turnstileToken || undefined
                })
            });

            const verifyData = await verifyRes.json();

            if (!verifyRes.ok || !verifyData.success) {
                if (verifyRes.status === 423 && verifyData.requires_dual_control) {
                    toast.dismiss(notifyId);
                    setSuspensionData({ isOpen: true, username: username.trim() });
                    return;
                }
                throw new Error(verifyData.error || 'Fallo en la verificación de la Passkey');
            }

            toast.success('¡Autenticación Biométrica Exitosa!', { id: notifyId, icon: '🛡️' });
            window.dispatchEvent(new Event('auth_changed'));
            window.location.href = '/master';

        } catch (err: any) {
            console.error('Error en Passkey Login:', err);
            toast.error(err.message || 'Operación biométrica cancelada o no soportada', { id: notifyId });
        } finally {
            setIsPasskeyLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-slate-50 flex flex-col justify-center items-center py-12 px-6 lg:px-8 font-sans overflow-y-auto">
            <LiquidAmbientBackground intensity="vibrant" />

            <div className="sm:mx-auto sm:w-full sm:max-w-md text-center relative z-10 animate-in fade-in slide-in-from-top-6 duration-1000">
                <div className="flex justify-center mb-6">
                    <div className="size-20 liquid-glass-card-light rounded-3xl flex flex-col items-center justify-center p-4 border border-blue-200 shadow-md shadow-blue-500/10">
                        <Shield className="text-blue-600 size-10 mb-1" />
                    </div>
                </div>
                <h2 className="text-3xl font-black text-slate-900 tracking-widest uppercase mb-2">
                    reDeFin<span className="text-blue-600">X</span> Master
                </h2>
                <div className="mt-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-50 border border-rose-200 shadow-xs">
                    <div className="size-2 bg-rose-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(244,63,94,0.6)]"></div>
                    <span className="text-[10px] text-rose-700 font-black uppercase tracking-[0.3em]">Auditoría Nivel 0</span>
                </div>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
                <div className="liquid-glass-card-light bg-white/95 p-10 rounded-[3rem] relative shadow-2xl border-white/80 space-y-6">
                    {/* Botón de Acceso Rápido con Passkey Biométrica */}
                    <div>
                        <button
                            type="button"
                            onClick={handlePasskeyLogin}
                            disabled={isPasskeyLoading || isLoading}
                            className="w-full py-3.5 px-5 rounded-2xl border border-indigo-200 bg-indigo-50/80 hover:bg-indigo-100 text-indigo-900 font-black text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2.5 shadow-xs cursor-pointer disabled:opacity-50"
                        >
                            {isPasskeyLoading ? (
                                <Loader2 className="size-4 animate-spin text-indigo-600" />
                            ) : (
                                <Fingerprint className="size-5 text-indigo-600" />
                            )}
                            Acceder con Passkey FIDO2
                        </button>
                    </div>

                    <div className="relative flex items-center justify-center">
                        <div className="border-t border-slate-200 w-full" />
                        <span className="bg-white px-3 text-[10px] font-black uppercase tracking-widest text-slate-400 shrink-0">
                            o con credencial institucional
                        </span>
                        <div className="border-t border-slate-200 w-full" />
                    </div>

                    <form className="space-y-4" onSubmit={handleLogin}>
                        <div className="space-y-1.5">
                            <label className="flex items-center gap-2 text-[11px] font-bold text-slate-700 uppercase tracking-wider ml-1">
                                <User className="size-3.5 text-blue-600" />
                                Usuario o Correo Institucional
                            </label>
                            <input
                                type="text"
                                required
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="liquid-glass-input-light block w-full px-5 py-3 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none text-sm font-mono tracking-tight"
                                placeholder="usuario@dominio"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="flex items-center gap-2 text-[11px] font-bold text-slate-700 uppercase tracking-wider ml-1">
                                <Lock className="size-3.5 text-blue-600" />
                                Credencial Criptográfica
                            </label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="liquid-glass-input-light block w-full px-5 py-3 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none text-lg font-bold tracking-[0.3em]"
                                placeholder="••••••••••••"
                            />
                        </div>

                        {/* Widget Cloudflare Turnstile Anti-Bot */}
                        <div className="pt-2">
                            <TurnstileWidget
                                onVerify={(token) => setTurnstileToken(token)}
                                onExpire={() => setTurnstileToken(null)}
                                onError={() => setTurnstileToken(null)}
                            />
                        </div>

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isLoading || !turnstileToken}
                                className={`w-full relative overflow-hidden flex justify-center items-center py-4 px-6 rounded-2xl transition-all bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black shadow-lg shadow-blue-500/25 cursor-pointer ${isLoading || !turnstileToken ? 'opacity-60 cursor-not-allowed' : 'hover:scale-[1.01]'}`}
                            >
                                <span className="flex items-center gap-3 text-xs font-black uppercase tracking-[0.2em] relative z-10">
                                    {isLoading ? <Loader2 className="size-5 animate-spin" /> : 'Desbloquear Bóveda'}
                                    {!isLoading && <ArrowRight className="size-4" />}
                                </span>
                            </button>
                        </div>
                    </form>
                </div>

                <div className="mt-8 text-center">
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.3em] mb-1">
                        Acceso Restringido a Infraestructura Crítica
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono">
                        PROTEGIDO POR CLOUDFLARE TURNSTILE & WEBAUTHN FIDO2 ({new Date().getFullYear()})
                    </p>
                </div>
            </div>

            {/* Modal de Suspensión por Vencimiento de 7 días (Fail-Closed) */}
            <SuspensionModal
                isOpen={suspensionData.isOpen}
                operatorId={suspensionData.operatorId}
                username={suspensionData.username}
                onLogout={() => setSuspensionData({ isOpen: false })}
            />
        </div>
    );
}
